import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

/**
 * Multi-select customer picker.
 *
 * Estate filters: empty selection means “all customers”.
 * User assignment: pass emptySummary / clearLabel / showFilterHint={false}.
 */
export function CustomerFilter({
  id = 'customer-filter',
  label = 'Customers',
  value = [],
  onChange,
  customers = [],
  disabled = false,
  className = '',
  emptySummary = 'All customers',
  clearLabel = 'Clear (show all)',
  showFilterHint = true,
}) {
  const [open, setOpen] = useState(false)
  const [menuStyle, setMenuStyle] = useState(null)
  const triggerRef = useRef(null)
  const menuRef = useRef(null)
  const selectedSet = useMemo(() => new Set(value.map(Number)), [value])

  const summary = useMemo(() => {
    if (value.length === 0) return emptySummary
    if (value.length === 1) {
      const c = customers.find((x) => x.atera_id === value[0])
      return c?.name || '1 customer'
    }
    return `${value.length} customers selected`
  }, [value, customers, emptySummary])

  function toggleCustomer(ateraId) {
    const idNum = Number(ateraId)
    if (selectedSet.has(idNum)) {
      onChange(value.filter((x) => Number(x) !== idNum))
    } else {
      onChange([...value, idNum])
    }
  }

  function selectAll() {
    onChange(customers.map((c) => c.atera_id))
  }

  function clearAll() {
    onChange([])
  }

  function updateMenuPosition() {
    const el = triggerRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const viewportPad = 8
    const preferredMax = 288 // ~max-h-72
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad
    const spaceAbove = rect.top - viewportPad
    const openUp = spaceBelow < 180 && spaceAbove > spaceBelow
    const maxHeight = Math.min(preferredMax, Math.max(160, openUp ? spaceAbove : spaceBelow))

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 260),
      maxHeight,
      zIndex: 80,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    })
  }

  useLayoutEffect(() => {
    if (!open) return
    updateMenuPosition()
  }, [open, customers.length, value.length])

  useEffect(() => {
    if (!open) return undefined
    function onReposition() {
      updateMenuPosition()
    }
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    return () => {
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    function onKey(event) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const listMaxHeight = menuStyle?.maxHeight
    ? Math.max(120, menuStyle.maxHeight - (showFilterHint && value.length > 0 ? 88 : 48))
    : 224

  const menu =
    open && menuStyle
      ? createPortal(
          <>
            <div
              className="fixed inset-0 z-[70]"
              aria-hidden
              onClick={() => setOpen(false)}
            />
            <div
              ref={menuRef}
              style={menuStyle}
              className="flex flex-col overflow-hidden rounded-md border border-border bg-card shadow-lg"
              role="listbox"
              aria-multiselectable="true"
            >
              <div className="flex shrink-0 items-center justify-between border-b border-border px-3 py-2 text-xs">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={selectAll}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-muted-foreground hover:underline"
                  onClick={clearAll}
                >
                  {clearLabel}
                </button>
              </div>
              <ul
                className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 text-sm"
                style={{ maxHeight: listMaxHeight }}
              >
                {customers.length === 0 ? (
                  <li className="px-2 py-3 text-muted-foreground">No customers available.</li>
                ) : (
                  customers.map((c) => (
                    <li key={c.atera_id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={selectedSet.has(c.atera_id)}
                          onChange={() => toggleCustomer(c.atera_id)}
                        />
                        <span className="truncate">{c.name}</span>
                      </label>
                    </li>
                  ))
                )}
              </ul>
              {showFilterHint && value.length > 0 ? (
                <p className="shrink-0 border-t border-border px-3 py-2 text-xs text-muted-foreground">
                  Showing data for {value.length} selected{' '}
                  {value.length === 1 ? 'customer' : 'customers'} only.
                </p>
              ) : null}
            </div>
          </>,
          document.body,
        )
      : null

  return (
    <div className={cn(label ? 'space-y-2' : '', className)}>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      <div ref={triggerRef} className="relative w-full max-w-md">
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled || !customers.length}
          className="h-10 w-full min-w-[220px] justify-between font-normal"
          aria-label={label || summary}
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => setOpen((o) => !o)}
        >
          <span className="truncate">{summary}</span>
          <ChevronDown className={cn('h-4 w-4 shrink-0 opacity-60', open && 'rotate-180')} />
        </Button>
        {menu}
      </div>
    </div>
  )
}
